// @flow
import React, { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { Box, Grid, Hidden, IconButton } from '@material-ui/core';
import Text from './common/Text';
import Image from './common/Image';
import StatusChip from './common/StatusChip';
import FixedMobileCTA from './common/FixedMobileCTA';
import ChangePaymentMethodModal from './ChangePaymentMethodModal';
import ChangeAddressModal from './ChangeAddressModal';
import AddressProcessingBanner from './common/AddressProcessingBanner';
import homeIcon from '../../../assets/images/home.svg';
import mapPinIcon from '../../../assets/images/map-pin.svg';
import { isPickupPointAddress } from '../../../utils/pickupPoint';
import { usePickupAddress } from './common/usePickupPoints';
import { Drawer, DrawerHeader } from './common/Drawer';
import { Radio } from '@material-ui/core';
import {
  DialogHeader,
  Dialog,
  DialogBody,
  DialogFooter,
} from './common/Dialog';

import { alphaCountryCodes, statusType } from '../constants';
import { describeStatus, getCardExpiryStatus } from '../utils/status';
import { formatPayementDetails } from '../utils/format';
import appStyles from '../../../config/appStyles.js';
import Button from './common/Button';
import axios from 'axios';
import { useStores } from '../stores';
import FluidImage from './common/FluidImage';
import TrustpilotReview, { isTrustpilotEligible } from './TrustpilotReview';

const useStyles = makeStyles((theme) => appStyles.subscriptionDetails(theme));

// Delivery type of the order address, as designed in the subscription details
const AddressTypeLabel = ({ pickup }) => {
  const classes = useStyles();
  const { t } = useTranslation(['components']);

  return (
    <Box className={classes.addressTypeLabel}>
      <img
        className={classes.addressTypeIcon}
        src={pickup ? mapPinIcon : homeIcon}
        alt=""
      />
      {pickup
        ? t('components:AddressesList.pickupDelivery')
        : t('components:AddressesList.homeDelivery')}
    </Box>
  );
};

const Panel = (props) => {
  const classes = useStyles();

  const { t } = useTranslation(['components']);
  const {
    highlight = true,
    className,
    title,
    label,
    value,
    status,
    editable = false,
    pending = false,
    // keep the edit button next to the value instead of centering it on a tall row
    alignBottom = false,
    onEdit,
    boxName,
    type,
    cardExpireStatus = false,
    orderId,
  } = props;
  const [showProcessingLabel, setProcessingLabel] = useState(true);
  const [showEditLabel, setEditLabel] = useState(false);
  useEffect(() => {
    function getCookie(cname) {
      let name = cname + '=';
      let decodedCookie = decodeURIComponent(document.cookie);
      let ca = decodedCookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) == ' ') {
          c = c.substring(1);
        }
        if (c.indexOf(name) == 0) {
          return JSON.parse(c.substring(name.length, c.length));
        }
      }
      return '';
    }
    const cookie = getCookie('processingLabelForPayment');
    const orderIds = Array.isArray(cookie?.orderIds) ? cookie.orderIds : [];
    // Show the label only on the collection(s) whose payment was changed.
    // Fallback to the old behaviour when the cookie has no orderIds (legacy).
    const isForThisOrder =
      orderIds.length === 0 || (orderId != null && orderIds.includes(orderId));

    if (type === 'payment' && cookie.isProcessing === 1 && isForThisOrder) {
      setProcessingLabel(false);
      setEditLabel(true);
    }
  }, [type, orderId]);

  return (
    <Box className={className}>
      <Box
        className={classes.panelWrapper}
        display="flex"
        justifyContent="space-between"
        alignItems={alignBottom ? 'flex-end' : 'center'}>
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          flexDirection="column"
          width="90%">
          <Text className={classes.panelTitle} vairant="titleL">
            {title}
          </Text>

          {!label?.toLowerCase().includes('free') &&
            title !== t('common:address') && (
              <Text variant="bodyM" className={highlight ? classes.label : ''}>
                {label?.toLowerCase() === 'default'
                  ? t('common:default')
                  : label}
              </Text>
            )}
          {status}

          {value && (
            <Text className={classes.value} variant="bodyM">
              {value}
            </Text>
          )}
          {cardExpireStatus && (
            <Text style={{ color: '#FF3658' }} variant="bodyXS">
              {cardExpireStatus}
            </Text>
          )}
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
          width="10%"
          fontSize={16}>
          {/* TODO: add  */}

          {type === 'payment' && showProcessingLabel && editable && (
            <IconButton onClick={onEdit}>
              <Image
                onClick={() => boxName == 'editPayment'}
                className={clsx(
                  classes.editIcon,
                  boxName == 'editPayment' && 'editPaymentBtnGTM',
                )}
                name="edit"
              />
            </IconButton>
          )}

          {type !== 'payment' && editable && (
            <IconButton onClick={onEdit}>
              <Image
                onClick={() => boxName == 'editPayment'}
                className={clsx(
                  classes.editIcon,
                  boxName == 'editPayment' && 'editPaymentBtnGTM',
                )}
                name="edit"
              />
            </IconButton>
          )}
        </Box>
      </Box>
      {type === 'payment' && (showEditLabel || pending) && (
        <Box
          width={'100%'}
          display={'flex'}
          flexDirection={'row'}
          justifyContent={'flex-end'}
          px={2}
          mb={2}>
          <StatusChip
            className={classes.statusInfo}
            type="pending"
            label={t('components:SubscriptionDetails.processing')}
          />
        </Box>
      )}

      {type !== 'payment' && pending && (
        <Box
          width={'100%'}
          display={'flex'}
          flexDirection={'row'}
          justifyContent={'flex-end'}
          px={2}
          mb={2}>
          <StatusChip
            className={classes.statusInfo}
            type="pending"
            label={t('components:SubscriptionDetails.processing')}
          />
        </Box>
      )}

      {type !== 'payment' && pending && (
        <Box
          width={'100%'}
          display={'flex'}
          flexDirection={'row'}
          justifyContent={'flex-end'}
          px={2}
          mb={2}>
          <StatusChip
            className={classes.statusInfo}
            type="pending"
            label={t('components:SubscriptionDetails.processing')}
          />
        </Box>
      )}
    </Box>
  );
};

const SubscriptionBody = observer((props) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation(['common', 'components']);

  const {
    className,
    subscription,
    address,
    onPaymentEdit,
    onAddressEdit,
    onPaymentEditAutoPayment,
    labelAddress,
    giftImg,
  } = props;

  const { payment } = subscription;
  const cardExpireStatus =
    payment?.method === 'CREDIT_CARD'
      ? getCardExpiryStatus(payment?.additionalInfo?.expire, t)
      : false;

  const planLabel = (offerType, deliveryPlan) => {
    if (
      offerType === 'PREMIUM' &&
      deliveryPlan.replace(/r/g, ' ') === 'FAST-TRACK'
    ) {
      return t('common:planLabel.FAST-TRACK-PREMIUM');
    } else if (!i18n.exists('common:planLabel.' + offerType)) {
      return offerType;
    } else {
      return t('common:planLabel.' + offerType);
    }
  };

  const { orderStore } = useStores();
  const { currentOrder, fetchPromoCodes, isAddressProcessing } = orderStore;
  const isPickupAddress = isPickupPointAddress(address);
  const { value: addressValue, label: pickupLabel } = usePickupAddress(
    address,
    isPickupAddress,
  );

  useEffect(() => {
    if (currentOrder) {
      fetchPromoCodes(currentOrder);
    }
  }, [currentOrder]);

  return (
    <Box className={clsx(classes.root, className)}>
      <Box
        p={3}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        className={classes.header}>
        <Text variant="titleXL" weight="bold" className={classes.headerTitle}>
          {t('components:SubscriptionDetails.title')}
        </Text>
      </Box>
      <Box className={classes.body}>
        <Box
          className={classes.currentOfferContainer}
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center">
          <div>
            <span className={classes.currentPlan}>
              {t('components:SubscriptionDetails.currentPlan')}
            </span>
            <span className={classes.currentOffer}>
              {planLabel(subscription.offerType, subscription?.deliveryPlan)}
            </span>
          </div>

          <div>
            <StatusChip
              className={classes.statusChip}
              {...describeStatus(statusType.ORDER, subscription.status)}
            />
          </div>
        </Box>
      </Box>

      {giftImg && payment?.method === 'OPEN_INVOICE' && (
        <Box mb={'6px'}>
          <h4 className={classes.upgradeAutoPaymentTitle}>
            {t('components:SubscriptionDetails.automaticPaymentTitle')}
          </h4>

          <p className={classes.upgradeAutoPaymentDescription}>
            {t('components:SubscriptionDetails.automaticPaymentDescription')}
          </p>

          <FluidImage
            className={classes.automaticImg}
            onLoad={false}
            src={giftImg}></FluidImage>

          <Button
            onClick={onPaymentEditAutoPayment}
            className={classes.btnUpgradeAutoPayment}>
            {t('components:SubscriptionDetails.automaticPaymentCTA')}
          </Button>
        </Box>
      )}

      <Box className={classes.infoBox}>
        <Grid container spacing={2}>
          {/* Order payment */}
          <Grid item xs={12}>
            <Box borderBottom={1} className={classes.borderGrey}>
              <Panel
                type="payment"
                orderId={subscription.id}
                boxName="editPayment"
                editable={!payment?.updating}
                pending={payment?.updating}
                onEdit={onPaymentEdit}
                title={t('common:paymentMethod')}
                label={t('components:Package.paidBy')}
                value={
                  payment?.method === 'CREDIT_CARD' ? (
                    <Trans
                      i18nKey="format:payment.details.CREDIT_CARD_HIGHLIGHTED"
                      values={{
                        type: t(
                          `format:payment.type.${payment?.additionalInfo?.type}`,
                        ),
                        summary: payment?.additionalInfo?.summary,
                        expire: payment?.additionalInfo?.expire,
                      }}
                      components={{
                        expire: (
                          <span
                            style={{
                              color: cardExpireStatus ? '#FF3658' : 'inherit',
                            }}
                          />
                        ),
                      }}
                    />
                  ) : (
                    formatPayementDetails(payment)
                  )
                }
                cardExpireStatus={cardExpireStatus}
              />
            </Box>
          </Grid>
          {/* Order address */}
          {address && (
            <Grid item xs={12}>
              <Box>
                <Panel
                  className={classes.addressPanel}
                  alignBottom
                  editable
                  onEdit={onAddressEdit}
                  title={t('common:address')}
                  label={labelAddress}
                  status={
                    <Box className={classes.addressStatus}>
                      {isAddressProcessing && (
                        <AddressProcessingBanner compact />
                      )}
                      <AddressTypeLabel pickup={isPickupAddress} />
                      {isPickupAddress && (
                        <Box className={classes.pickupPointName}>
                          {pickupLabel}
                        </Box>
                      )}
                    </Box>
                  }
                  value={addressValue}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
});

const SubscriptionBodyMobile = observer((props) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation(['common', 'components']);
  const [isDeliveryPlanPopupOpen, setDeliveryPlanPopupOpen] = useState(false);

  const { uiStore, userStore, helpStore, orderStore, addressStore } =
    useStores();
  const { user } = userStore;
  const {
    currentOrder,
    shipments,
    fetchPromoCodes,
    promoCodes,
    isAddressProcessing,
  } = orderStore;

  const [showDeliveryPlan, setShowDeliveryPlan] = useState(true);

  useEffect(() => {
    if (currentOrder) {
      fetchPromoCodes(currentOrder);
    }
  }, [currentOrder]);

  const setCookie = (c_name, c_value, exdays) => {
    var exdate = new Date();
    exdate.setDate(exdate.getDate() + exdays);
    document.cookie =
      encodeURIComponent(c_name) +
      '=' +
      encodeURIComponent(c_value) +
      (!exdays ? '' : '; expires=' + exdate.toUTCString());
  };

  useEffect(() => {
    function getCookie(cname) {
      let name = cname + '=';
      let decodedCookie = decodeURIComponent(document.cookie);
      let ca = decodedCookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) == ' ') {
          c = c.substring(1);
        }
        if (c.indexOf(name) == 0) {
          return JSON.parse(c.substring(name.length, c.length));
        }
      }
      return '';
    }

    const cookie = getCookie('deliveryPlanPopup');
    if (cookie.show === 1) {
      setShowDeliveryPlan(false);
    }
  }, [isDeliveryPlanPopupOpen]);

  const handleSubmitDeliveryPlan = () => {
    uiStore.setLoader(true);

    let paramsContact = {
      message:
        'Issue per package: 8 issues / package Price per package: regular price: 10,90€',
      email: user.email,
      subtopic: `Current open package: ${
        shipments.open &&
        shipments.open.length > 0 &&
        shipments.open[0].sequence
      }`,
      topic: 'Request a new delivery plan',
      products: [currentOrder],
    };

    helpStore
      .createTicket(paramsContact)
      .then((res) => {
        if (res) {
          uiStore.openModal(
            'Grazie mille! Abbiamo registrato la tua richiesta per cambiare il tuo piano di spedizione. il nostro Servizio Clienti la prenderà in carico e ti informerà non appena i cambiamenti saranno effettivi',
            false,
          );
          setCookie(
            'deliveryPlanPopup',
            JSON.stringify({
              show: 1,
            }),
            999,
          );
          setDeliveryPlanPopupOpen(false);
          // resetForm();
        } else {
          uiStore.showGenericError();
        }
        uiStore.setLoader(false);
      })
      .catch((err) => {
        uiStore.showGenericError();
        uiStore.setLoader(false);
      });
  };

  const {
    className,
    subscription,
    address,
    onPaymentEdit,
    onPaymentEditAutoPayment,
    onAddressEdit,
    labelAddress,
    giftImg,
    showTrustpilot,
  } = props;

  const isPickupAddress = isPickupPointAddress(address);
  const { value: addressValue, label: pickupLabel } = usePickupAddress(
    address,
    isPickupAddress,
  );

  const { payment } = subscription;
  const cardExpireStatus =
    payment?.method === 'CREDIT_CARD'
      ? getCardExpiryStatus(payment?.additionalInfo?.expire, t)
      : false;

  const checkThisFCKDelivery = (shipments) => {
    if (shipments && shipments.open && shipments.open.length > 0) {
      if (
        shipments.open[0].sequence == 16 ||
        shipments.open[0].sequence == 18 ||
        shipments.open[0].sequence == 20
      ) {
        return true;
      } else {
        return false;
      }
    }

    if (shipments && shipments.closed && shipments.closed.length > 0) {
      if (
        shipments.closed[0].sequence == 16 ||
        shipments.closed[0].sequence == 18 ||
        shipments.closed[0].sequence == 20
      ) {
        return true;
      }
    }

    return false;
  };

  const planLabel = (offerType, deliveryPlan) => {
    if (
      offerType === 'PREMIUM' &&
      deliveryPlan.replace(/r/g, ' ') === 'FAST-TRACK'
    ) {
      return t('common:planLabel.FAST-TRACK-PREMIUM');
    } else if (!i18n.exists('common:planLabel.' + offerType)) {
      return offerType;
    } else {
      return t('common:planLabel.' + offerType);
    }
  };

  return (
    <Box className={clsx(classes.root, className)}>
      <Dialog
        className={classes.modal}
        open={isDeliveryPlanPopupOpen}
        id="change-country-modal"
        maxWidth="md"
        BackdropProps={{
          classes: {
            root: classes.backDrop,
          },
        }}
        PaperProps={{
          classes: {
            root: classes.paperProps,
          },
        }}>
        <DialogHeader
          title={'Piano di spedizione'}
          onClose={() => setDeliveryPlanPopupOpen(false)}
          className={classes.modalHeader}
        />
        <DialogBody className={classes.modalBody}>
          <Text className={classes.deliveryTitle}>
            Il tuo attuale piano di spedizione
          </Text>
          <Box
            mb={3}
            display="flex"
            flexDirection="row"
            className={classes.deliveryPlanBox}>
            <Box display="flex" flexDirection="column">
              <Text className={classes.deliverySmall}>
                Numero di uscite per ogni spedizione
              </Text>
              <Text className={classes.deliveryTitle}>
                5 numeri / spedizione
              </Text>
            </Box>
            <Box display="flex" flexDirection="column">
              <Text className={classes.deliverySmall}>
                Prezzo per ogni uscita
              </Text>
              <Text className={classes.deliveryTitle}>
                Prezzo regolare 10,99€
              </Text>
            </Box>
          </Box>
          {/* 2nd col */}
          <Text className={classes.deliveryTitle}>
            Richiedi un nuovo piano di spedizione
          </Text>
          <Text>Ricevi più velocemente la tua collezione!</Text>
          <Box
            display="flex"
            flexDirection="row"
            className={classes.deliveryPlanBox}>
            <Radio checked={true}></Radio>
            <Box display="flex" flexDirection="column">
              <Text className={classes.deliverySmall}>
                Numero di uscite per ogni spedizione
              </Text>
              <Text className={classes.deliveryTitle}>
                8 numeri/ spedizione
              </Text>
            </Box>
            <Box display="flex" flexDirection="column">
              <Text className={classes.deliverySmall}>
                Prezzo per ogni uscita
              </Text>
              <Text className={classes.deliveryTitle}>
                Prezzo regolare 10,99€
              </Text>
            </Box>
          </Box>
        </DialogBody>
        <DialogFooter className={classes.dialogFooterSingleOrder}>
          <Box
            justifyContent="flex-end"
            p={2}
            width="100%"
            display="flex"
            className={classes.buttonContainer}
            style={{ gap: '16px' }}>
            <Button
              variant="outlined"
              onClick={() => {
                setDeliveryPlanPopupOpen(false);
              }}>
              Cancella
            </Button>
            <Button
              className={classes.buttonDebt}
              onClick={() => {
                handleSubmitDeliveryPlan();
              }}>
              Inviaci la richietsa
            </Button>
          </Box>
        </DialogFooter>
      </Dialog>
      <Box
        className={classes.currentOfferContainer}
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center">
        <Box>
          <span className={classes.currentPlan}>
            {t('components:SubscriptionDetails.currentPlan')}
          </span>
          <span className={classes.currentOffer}>
            {planLabel(subscription.offerType, subscription?.deliveryPlan)}
            {/* {subscription.offerType} */}
          </span>
        </Box>
        <Box>
          <StatusChip
            className={classes.statusChip}
            {...describeStatus(statusType.ORDER, subscription.status)}
          />
        </Box>
      </Box>

      {/* Automatic payment for mobile */}
      {giftImg && payment?.method === 'OPEN_INVOICE' && (
        <Box mb={'6px'}>
          <h4 className={classes.upgradeAutoPaymentTitle}>
            {t('components:SubscriptionDetails.automaticPaymentTitle')}
          </h4>

          <p className={classes.upgradeAutoPaymentDescription}>
            {t('components:SubscriptionDetails.automaticPaymentDescription')}
          </p>
          <FluidImage
            className={classes.automaticImg}
            onLoad={false}
            src={giftImg}></FluidImage>
          <Button
            onClick={onPaymentEditAutoPayment}
            className={classes.btnUpgradeAutoPayment}>
            {t('components:SubscriptionDetails.automaticPaymentCTA')}
          </Button>
        </Box>
      )}

      {/* Order payment */}
      <Box borderBottom={1} className={classes.borderGrey}>
        <Panel
          type="payment"
          orderId={subscription.id}
          boxName="editPayment"
          editable={!payment.updating}
          pending={payment.updating}
          onEdit={onPaymentEdit}
          title={t('common:paymentMethod')}
          label={t('components:Package.paidBy')}
          value={
            payment?.method === 'CREDIT_CARD' ? (
              <Trans
                i18nKey="format:payment.details.CREDIT_CARD_HIGHLIGHTED"
                values={{
                  type: t(
                    `format:payment.type.${payment?.additionalInfo?.type}`,
                  ),
                  summary: payment?.additionalInfo?.summary,
                  expire: payment?.additionalInfo?.expire,
                }}
                components={{
                  expire: (
                    <span
                      style={{
                        color: cardExpireStatus ? '#FF3658' : 'inherit',
                      }}
                    />
                  ),
                }}
              />
            ) : (
              formatPayementDetails(payment)
            )
          }
          cardExpireStatus={cardExpireStatus}
        />
      </Box>
      {/* Order address */}
      {address && (
        <Box
          borderBottom={promoCodes.length > 0 ? 1 : 0}
          className={classes.borderGrey}>
          <Panel
            className={classes.addressPanel}
            alignBottom
            editable
            onEdit={onAddressEdit}
            title={t('common:address')}
            label={labelAddress}
            status={
              <Box className={classes.addressStatus}>
                {isAddressProcessing && <AddressProcessingBanner compact />}
                <AddressTypeLabel pickup={isPickupAddress} />
                {isPickupAddress && (
                  <Box className={classes.pickupPointName}>
                    {pickupLabel}
                  </Box>
                )}
              </Box>
            }
            value={addressValue}
            // value={formatAddress(address)}
          />
        </Box>
      )}

      {/* Se ci sono gli open e sono minore uguale di 16  e ci sono i closed e sono uguali a 16|| se non ci sono gli open e i closed sono uguale a 16  */}
      {checkThisFCKDelivery(shipments) &&
        showDeliveryPlan &&
        uiStore.site === 'it' &&
        currentOrder?.productId === '9X5866000000' &&
        currentOrder.variantAttributes.plan !== 'FT' &&
        currentOrder?.payment?.method !== 'OPEN_INVOICE' && (
          <Hidden mdUp>
            <Box className={classes.deliveryPlanContainer} pt={2}>
              <Box
                className={clsx(classes.panelWrapperDeliveryPlan)}
                display="flex"
                justifyContent="space-between"
                alignItems="center">
                <Box
                  display="flex"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  flexDirection="column"
                  width="90%">
                  <Text className={classes.panelTitle} vairant="titleL">
                    Piano di spedizione
                  </Text>
                  <Text variant="bodyM" className={true ? classes.label : ''}>
                    Standard
                  </Text>

                  <Box mt={3}>
                    <Text className={classes.panelTitle} vairant="titleL">
                      Ricevi più velocemente la tua collezione!
                    </Text>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Button
              className={classes.btnDeliveryPlan}
              onClick={() => {
                setDeliveryPlanPopupOpen(!isDeliveryPlanPopupOpen);
              }}>
              Aggiorna il tuo piano di spedizione
            </Button>
          </Hidden>
        )}

      {showTrustpilot && (
        <Hidden mdUp>
          <TrustpilotReview className={classes.deliveryPlanContainer} />
        </Hidden>
      )}
    </Box>
  );
});

const SubscriptionDetails = observer(({ subscription, address, productId }) => {
  const classes = useStyles();

  const { uiStore, userStore, helpStore, orderStore } = useStores();
  const { addresses, user } = userStore;
  const { currentOrder, shipments } = orderStore;

  const [giftImg, setGiftImg] = useState();
  const [openDrawer, setOpenDrawer] = useState();
  const [editPayment, setEditPayment] = useState(false);
  const [editPaymentAuto, setEditPaymentAuto] = useState(false);
  const [editAddr, setEditAddr] = useState(false);
  const [defaultAddr, setDefaultAddr] = useState();
  const [isDeliveryPlanPopupOpen, setDeliveryPlanPopupOpen] = useState(false);
  const [showDeliveryPlan, setShowDeliveryPlan] = useState(true);
  const showTrustpilot = isTrustpilotEligible(currentOrder, shipments);

  useEffect(() => {
    axios
      .get(
        process.env.REACT_APP_SNAKE_APIS +
          `product/gift?code=${productId}&country=${uiStore.site}`,
      )
      .then((res) => {
        setGiftImg(res.data.image);
      });
  }, []);

  useEffect(() => {
    if (addresses) {
      //  find the default addr
      const defaultAddr = addresses.find((a) => a.defaultAddress);
      // select the default addr
      if (defaultAddr) {
        setDefaultAddr(defaultAddr.label);
      }
    }
  }, [addresses]);

  const toggleOpenDrawer = () => {
    setOpenDrawer(!openDrawer);
  };

  const toggleEditPayment = () => {
    setEditPayment(!editPayment);
    // setEditPaymentAuto(!editPaymentAuto);
  };

  const toggleOnPaymentEditAutoPayment = () => {
    setEditPayment(!editPayment);
    setEditPaymentAuto(!editPaymentAuto);
  };

  const toggleEditAddr = () => {
    setEditAddr(!editAddr);
  };

  const { t, i18n } = useTranslation(['components']);

  const setCookie = (c_name, c_value, exdays) => {
    var exdate = new Date();
    exdate.setDate(exdate.getDate() + exdays);
    document.cookie =
      encodeURIComponent(c_name) +
      '=' +
      encodeURIComponent(c_value) +
      (!exdays ? '' : '; expires=' + exdate.toUTCString());
  };

  const handleSubmitDeliveryPlan = () => {
    uiStore.setLoader(true);

    let paramsContact = {
      message:
        'Issue per package: 8 issues / package Price per package: regular price: 10,90€',
      email: user.email,
      subtopic: `Current open package: ${
        shipments.open &&
        shipments.open.length > 0 &&
        shipments.open[0].sequence
      }`,
      topic: 'Request a new delivery plan',
      products: [currentOrder],
    };

    helpStore
      .createTicket(paramsContact)
      .then((res) => {
        if (res) {
          uiStore.openModal(
            'Grazie mille! Abbiamo registrato la tua richiesta per cambiare il tuo piano di spedizione. il nostro Servizio Clienti la prenderà in carico e ti informerà non appena i cambiamenti saranno effettivi',
            false,
          );
          setCookie(
            'deliveryPlanPopup',
            JSON.stringify({
              show: 1,
            }),
            999,
          );
          setDeliveryPlanPopupOpen(false);
          // resetForm();
        } else {
          uiStore.showGenericError();
        }
        uiStore.setLoader(false);
      })
      .catch((err) => {
        uiStore.showGenericError();
        uiStore.setLoader(false);
      });
  };

  useEffect(() => {
    function getCookie(cname) {
      let name = cname + '=';
      let decodedCookie = decodeURIComponent(document.cookie);
      let ca = decodedCookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) == ' ') {
          c = c.substring(1);
        }
        if (c.indexOf(name) == 0) {
          return JSON.parse(c.substring(name.length, c.length));
        }
      }
      return '';
    }

    const cookie = getCookie('deliveryPlanPopup');
    if (cookie.show === 1) {
      setShowDeliveryPlan(false);
    }
  }, [isDeliveryPlanPopupOpen]);

  const checkThisFCKDelivery = (shipments) => {
    if (shipments && shipments.open && shipments.open.length > 0) {
      if (
        shipments.open[0].sequence == 16 ||
        shipments.open[0].sequence == 18 ||
        shipments.open[0].sequence == 20
      ) {
        return true;
      } else {
        return false;
      }
    }

    if (shipments && shipments.closed && shipments.closed.length > 0) {
      if (
        shipments.closed[0].sequence == 16 ||
        shipments.closed[0].sequence == 18 ||
        shipments.closed[0].sequence == 20
      ) {
        return true;
      }
    }

    return false;
  };

  return (
    <div id="subscription-details">
      <Dialog
        className={classes.modal}
        open={isDeliveryPlanPopupOpen}
        id="change-country-modal"
        maxWidth="md"
        BackdropProps={{
          classes: {
            root: classes.backDrop,
          },
        }}
        PaperProps={{
          classes: {
            root: classes.paperProps,
          },
        }}>
        <DialogHeader
          title={'Piano di spedizione'}
          onClose={() => setDeliveryPlanPopupOpen(false)}
          className={classes.modalHeader}
        />
        <DialogBody className={classes.modalBody}>
          <Text className={classes.deliveryTitle}>
            Il tuo attuale piano di spedizione
          </Text>
          <Box
            mb={3}
            display="flex"
            flexDirection="row"
            className={classes.deliveryPlanBox}>
            <Box display="flex" flexDirection="column">
              <Text className={classes.deliverySmall}>
                Numero di uscite per ogni spedizione
              </Text>
              <Text className={classes.deliveryTitle}>
                4 numeri / spedizione
              </Text>
            </Box>
            <Box display="flex" flexDirection="column">
              <Text className={classes.deliverySmall}>
                Prezzo per ogni uscita
              </Text>
              <Text className={classes.deliveryTitle}>
                Prezzo regolare 10,99€
              </Text>
            </Box>
          </Box>
          {/* 2nd col */}
          <Text className={classes.deliveryTitle}>
            Richiedi un nuovo piano di spedizione
          </Text>
          <Text>Ricevi più velocemente la tua collezione!</Text>
          <Box
            display="flex"
            flexDirection="row"
            className={classes.deliveryPlanBox}>
            <Radio checked={true}></Radio>
            <Box display="flex" flexDirection="column">
              <Text className={classes.deliverySmall}>
                Numero di uscite per ogni spedizione
              </Text>
              <Text className={classes.deliveryTitle}>
                8 numeri/ spedizione
              </Text>
            </Box>
            <Box display="flex" flexDirection="column">
              <Text className={classes.deliverySmall}>
                Prezzo per ogni uscita
              </Text>
              <Text className={classes.deliveryTitle}>
                Prezzo regolare 10,99€
              </Text>
            </Box>
          </Box>
        </DialogBody>
        <DialogFooter className={classes.dialogFooterSingleOrder}>
          <Box
            justifyContent="flex-end"
            p={2}
            width="100%"
            display="flex"
            className={classes.buttonContainer}
            style={{ gap: '16px' }}>
            <Button
              variant="outlined"
              onClick={() => {
                setDeliveryPlanPopupOpen(false);
              }}>
              Cancella
            </Button>
            <Button
              className={classes.buttonDebt}
              onClick={() => {
                handleSubmitDeliveryPlan();
              }}>
              Inviaci la richietsa
            </Button>
          </Box>
        </DialogFooter>
      </Dialog>

      <Hidden mdUp>
        <FixedMobileCTA
          label={t('components:SubscriptionDetails.title')}
          handleOnClick={toggleOpenDrawer}
        />
      </Hidden>
      <Hidden smDown>
        <SubscriptionBody
          giftImg={giftImg}
          address={address}
          subscription={subscription}
          onPaymentEditAutoPayment={toggleOnPaymentEditAutoPayment}
          onPaymentEdit={toggleEditPayment}
          onAddressEdit={toggleEditAddr}
          labelAddress={defaultAddr}
        />
      </Hidden>
      <Drawer
        className={classes.modal}
        anchor={'bottom'}
        open={openDrawer}
        ModalProps={{
          disableAutoFocus: true,
          disableEnforceFocus: true,
        }}
        classes={{ paper: classes.drawerContent }}>
        <DrawerHeader
          className={classes.drawnerHeaderTitle}
          title={t('components:SubscriptionDetails.title')}
          onClose={toggleOpenDrawer}
        />
        <SubscriptionBodyMobile
          giftImg={giftImg}
          className={classes.drawerInfo}
          address={address}
          subscription={subscription}
          onPaymentEditAutoPayment={toggleOnPaymentEditAutoPayment}
          onPaymentEdit={toggleEditPayment}
          onAddressEdit={toggleEditAddr}
          labelAddress={defaultAddr}
          showTrustpilot={showTrustpilot}
        />
      </Drawer>
      {alphaCountryCodes[user.billingCountry] && (
        <ChangePaymentMethodModal
          open={editPayment}
          editPaymentAuto={editPaymentAuto}
          subscription={subscription}
          onClose={toggleEditPayment}
        />
      )}
      <ChangeAddressModal
        open={editAddr}
        subscription={subscription}
        address={address}
        onClose={toggleEditAddr}
      />

      {checkThisFCKDelivery(shipments) &&
        showDeliveryPlan &&
        uiStore.site === 'it' &&
        currentOrder?.productId === '9X5866000000' &&
        currentOrder.variantAttributes.plan === 'RE' &&
        currentOrder?.payment?.method !== 'OPEN_INVOICE' && (
          <Hidden smDown>
            <Box className={classes.deliveryPlanContainer} pt={2}>
              <Box
                className={clsx(classes.panelWrapperDeliveryPlan)}
                display="flex"
                justifyContent="space-between"
                alignItems="center">
                <Box
                  display="flex"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  flexDirection="column"
                  width="90%">
                  <Text className={classes.panelTitle} vairant="titleL">
                    Piano di spedizione
                  </Text>
                  <Text variant="bodyM" className={true ? classes.label : ''}>
                    Standard
                  </Text>

                  <Box mt={3}>
                    <Text className={classes.panelTitle} vairant="titleL">
                      Ricevi più velocemente la tua collezione!
                    </Text>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Button
              className={classes.btnDeliveryPlan}
              onClick={() => {
                setDeliveryPlanPopupOpen(!isDeliveryPlanPopupOpen);
              }}>
              Aggiorna il tuo piano di spedizione
            </Button>
          </Hidden>
        )}

      {showTrustpilot && (
        <Hidden smDown>
          <TrustpilotReview className={classes.deliveryPlanContainer} />
        </Hidden>
      )}
    </div>
  );
});

export default SubscriptionDetails;
